import { createContext, useContext, useState, useEffect, useRef, useMemo, ReactNode, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { api } from '../../lib/api';
import { useAuth } from '../auth/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { SIDEBAR_PERMISSION_MAP } from '@dental-manager/shared';

// Types
export interface ModuleDefinition {
  id: string;
  name: string;
  description: string;
  icon: string;
  basePath: string;
  tier: 'free' | 'standard' | 'premium' | 'enterprise';
  color?: string;
}

export interface SidebarItem {
  label: string;
  href: string;
  icon: string;
  badge?: number;
  end?: boolean;
}

export interface ModuleSidebarConfig {
  moduleId: string;
  sections: {
    title?: string;
    items: SidebarItem[];
  }[];
}

interface ModuleContextType {
  currentModule: ModuleDefinition | null;
  currentSidebar: ModuleSidebarConfig | null;
  availableModules: ModuleDefinition[];
  allEntityModules: ModuleDefinition[];
  setCurrentModule: (moduleId: string | null) => void;
}

const ModuleContext = createContext<ModuleContextType | undefined>(undefined);

// Default modules configuration
const defaultModules: ModuleDefinition[] = [
  {
    id: 'stock',
    name: 'Stock',
    description: 'modules.descriptions.stock',
    icon: 'Package',
    basePath: '/stock',
    tier: 'standard',
    color: '#0d9488',
  },
  {
    id: 'devis',
    name: 'Devis',
    description: 'modules.descriptions.devis',
    icon: 'FileText',
    basePath: '/devis',
    tier: 'standard',
    color: '#8b5cf6',
  },
  {
    id: 'banking',
    name: 'Banque',
    description: 'modules.descriptions.banking',
    icon: 'Building',
    basePath: '/banking',
    tier: 'premium',
    color: '#0ea5e9',
  },
  {
    id: 'analytics',
    name: 'Analyse',
    description: 'modules.descriptions.analytics',
    icon: 'BarChart3',
    basePath: '/analytics',
    tier: 'standard',
    color: '#f59e0b',
  },
  {
    id: 'redactia',
    name: 'RedactIA',
    description: 'modules.descriptions.redactia',
    icon: 'FileText',
    basePath: '/redactia',
    tier: 'premium',
    color: '#7c3aed',
  },
  {
    id: 'repondeur',
    name: 'Répondeur',
    description: 'modules.descriptions.repondeur',
    icon: 'PhoneCall',
    basePath: '/repondeur',
    tier: 'standard',
    color: '#0ea5e9',
  },
  {
    id: 'equipe',
    name: 'Équipe',
    description: 'modules.descriptions.equipe',
    icon: 'UserCheck',
    basePath: '/equipe',
    tier: 'standard',
    color: '#10b981',
  },
  {
    id: 'admin',
    name: 'Administrateur',
    description: 'modules.descriptions.admin',
    icon: 'Shield',
    basePath: '/admin',
    tier: 'free',
    color: '#64748b',
  },
  {
    id: 'zilloteam',
    name: 'ZilloTalk',
    description: 'modules.descriptions.zilloteam',
    icon: 'MessageSquare',
    basePath: '/zilloteam',
    tier: 'standard',
    color: '#8b5cf6',
  },
  {
    id: 'zillotasks',
    name: 'ZilloTasks',
    description: 'modules.descriptions.zillotasks',
    icon: 'CheckSquare',
    basePath: '/zillotasks',
    tier: 'standard',
    color: '#7c3aed',
  },
  {
    id: 'zillodocs',
    name: 'ZilloDocs',
    description: 'modules.descriptions.zillodocs',
    icon: 'FileSearch',
    basePath: '/zillodocs',
    tier: 'standard',
    color: '#f59e0b',
  },
  {
    id: 'zillotv',
    name: 'ZilloTV',
    description: 'modules.descriptions.zillotv',
    icon: 'Tv',
    basePath: '/zillo-tv',
    tier: 'standard',
    color: '#10b981',
  },
];

// Default sidebar configurations per module
const defaultSidebars: Record<string, ModuleSidebarConfig> = {
  stock: {
    moduleId: 'stock',
    sections: [
      {
        title: 'sidebar.sections.stock',
        items: [
          { label: 'sidebar.items.dashboard', href: '/stock', icon: 'LayoutDashboard' },
          { label: 'sidebar.items.stockExit', href: '/stock/sortie', icon: 'ScanBarcode' },
          { label: 'sidebar.items.products', href: '/stock/products', icon: 'Package' },
          { label: 'sidebar.items.archivedProducts', href: '/stock/products/archived', icon: 'Archive' },
          { label: 'sidebar.items.suppliers', href: '/stock/suppliers', icon: 'Truck' },
        ],
      },
      {
        title: 'sidebar.sections.orders',
        items: [
          { label: 'sidebar.items.cart', href: '/stock/cart', icon: 'ShoppingCart' },
          { label: 'sidebar.items.orders', href: '/stock/orders', icon: 'ClipboardList' },
          { label: 'sidebar.items.smartOrder', href: '/stock/smart-order', icon: 'Sparkles' },
          { label: 'sidebar.items.reception', href: '/stock/reception', icon: 'PackageCheck' },
          { label: 'sidebar.items.invoices', href: '/stock/invoices', icon: 'FileText' },
        ],
      },
      {
        title: 'sidebar.sections.analyse',
        items: [
          { label: 'sidebar.items.expirations', href: '/stock/expirations', icon: 'Clock' },
          { label: 'sidebar.items.predictions', href: '/stock/predictions', icon: 'Activity' },
          { label: 'sidebar.items.anomalies', href: '/stock/anomalies', icon: 'AlertTriangle' },
          { label: 'sidebar.items.journal', href: '/stock/journal', icon: 'ScrollText' },
          { label: 'sidebar.items.exportComptable', href: '/stock/export', icon: 'FileText' },
        ],
      },
    ],
  },
  devis: {
    moduleId: 'devis',
    sections: [
      {
        title: 'sidebar.sections.devis',
        items: [
          { label: 'sidebar.items.dashboard', href: '/devis', icon: 'LayoutDashboard' },
          { label: 'sidebar.items.newDevis', href: '/devis/new', icon: 'Plus' },
          { label: 'sidebar.items.inProgress', href: '/devis/pending', icon: 'Clock' },
          { label: 'sidebar.items.history', href: '/devis/history', icon: 'History' },
        ],
      },
    ],
  },
  banking: {
    moduleId: 'banking',
    sections: [
      {
        title: 'sidebar.sections.banking',
        items: [
          { label: 'sidebar.items.dashboard', href: '/banking', icon: 'LayoutDashboard' },
          { label: 'sidebar.items.transactions', href: '/banking/transactions', icon: 'ArrowLeftRight' },
          { label: 'sidebar.items.reconciliation', href: '/banking/reconciliation', icon: 'CheckSquare' },
          { label: 'sidebar.items.connections', href: '/banking/connections', icon: 'Link' },
        ],
      },
    ],
  },
  analytics: {
    moduleId: 'analytics',
    sections: [
      {
        title: 'sidebar.sections.analyse',
        items: [
          { label: 'sidebar.items.overview', href: '/analytics', icon: 'BarChart3' },
          { label: 'sidebar.items.activity', href: '/analytics/activity', icon: 'Activity' },
          { label: 'sidebar.items.financial', href: '/analytics/financial', icon: 'Euro' },
          { label: 'sidebar.items.patients', href: '/analytics/patients', icon: 'Users' },
        ],
      },
    ],
  },
  redactia: {
    moduleId: 'redactia',
    sections: [
      {
        title: 'sidebar.sections.redactia',
        items: [
          { label: 'sidebar.items.dashboard', href: '/redactia', icon: 'LayoutDashboard', end: true },
          { label: 'sidebar.items.patients', href: '/redactia/patients', icon: 'Users' },
          { label: 'sidebar.items.documents', href: '/redactia/documents', icon: 'FileText' },
          { label: 'sidebar.items.clinicalBuilder', href: '/redactia/clinical-builder', icon: 'Stethoscope' },
          { label: 'sidebar.items.checklists', href: '/redactia/checklists', icon: 'ClipboardList' },
          { label: 'sidebar.items.templates', href: '/redactia/templates', icon: 'Layout' },
          { label: 'sidebar.items.documentTemplates', href: '/redactia/document-templates', icon: 'LayoutTemplate' },
          { label: 'sidebar.items.statistics', href: '/redactia/stats', icon: 'BarChart2' },
        ],
      },
    ],
  },
  repondeur: {
    moduleId: 'repondeur',
    sections: [
      {
        title: 'sidebar.sections.messaging',
        items: [
          { label: 'sidebar.items.dashboard', href: '/repondeur', icon: 'LayoutDashboard' },
          { label: 'sidebar.items.messages', href: '/repondeur/messages', icon: 'PhoneCall' },
          { label: 'sidebar.items.archived', href: '/repondeur/archives', icon: 'Archive' },
        ],
      },
      {
        title: 'sidebar.sections.audioStudio',
        items: [
          { label: 'sidebar.items.createMessage', href: '/repondeur/studio', icon: 'Mic' },
          { label: 'sidebar.items.generatedMessages', href: '/repondeur/studio/messages', icon: 'AudioLines' },
        ],
      },
    ],
  },
  zilloteam: {
    moduleId: 'zilloteam',
    sections: [
      {
        items: [
          { label: 'sidebar.items.dashboard', href: '/zilloteam', icon: 'LayoutDashboard', end: true },
        ],
      },
      {
        title: 'sidebar.sections.messaging',
        items: [
          { label: 'sidebar.items.discussions', href: '/zilloteam/discussions', icon: 'MessageSquare' },
          { label: 'sidebar.items.newConversation', href: '/zilloteam/new', icon: 'Plus' },
        ],
      },
      {
        title: 'sidebar.sections.directory',
        items: [
          { label: 'sidebar.items.patients', href: '/zilloteam/patients', icon: 'UserRound' },
          { label: 'sidebar.items.partners', href: '/zilloteam/partenaires', icon: 'Users' },
        ],
      },
    ],
  },
  zillotasks: {
    moduleId: 'zillotasks',
    sections: [
      {
        title: 'sidebar.sections.tasks',
        items: [
          { label: 'sidebar.items.myTasks', href: '/zillotasks', icon: 'CheckSquare', end: true },
          { label: 'sidebar.items.general', href: '/zillotasks/general', icon: 'Inbox' },
        ],
      },
    ],
  },
  zillodocs: {
    moduleId: 'zillodocs',
    sections: [
      {
        title: 'sidebar.sections.documents',
        items: [
          { label: 'sidebar.items.allDocuments', href: '/zillodocs', icon: 'FileSearch', end: true },
          { label: 'sidebar.items.confidential', href: '/zillodocs/confidential', icon: 'Lock' },
          { label: 'sidebar.items.correspondents', href: '/zillodocs/correspondants', icon: 'Users' },
          { label: 'sidebar.items.tags', href: '/zillodocs/tags', icon: 'Tag' },
          { label: 'sidebar.items.bulkImport', href: '/zillodocs/bulk-import', icon: 'HardDriveUpload' },
          { label: 'sidebar.items.activity', href: '/zillodocs/activity', icon: 'Activity' },
        ],
      },
    ],
  },
  equipe: {
    moduleId: 'equipe',
    sections: [
      {
        title: 'sidebar.sections.administration',
        items: [
          { label: 'sidebar.items.dashboard', href: '/equipe', icon: 'LayoutDashboard' },
          { label: 'sidebar.items.hrReporting', href: '/equipe/reporting', icon: 'PieChart' },
          { label: 'sidebar.items.employees', href: '/equipe/employes', icon: 'UserCheck' },
          { label: 'sidebar.items.alerts', href: '/equipe/alertes', icon: 'AlertTriangle' },
        ],
      },
      {
        title: 'sidebar.sections.presence',
        items: [
          { label: 'sidebar.items.time', href: '/equipe/temps', icon: 'Timer' },
          { label: 'sidebar.items.planning', href: '/equipe/planning', icon: 'Calendar' },
          { label: 'sidebar.items.leaveAbsences', href: '/equipe/conges', icon: 'Umbrella' },
        ],
      },
      {
        title: 'sidebar.sections.payroll',
        items: [
          { label: 'sidebar.items.payroll', href: '/equipe/paie', icon: 'FileText' },
          { label: 'sidebar.items.remuneration', href: '/equipe/remuneration', icon: 'Euro' },
          { label: 'sidebar.items.compliance', href: '/equipe/conformite', icon: 'ShieldCheck' },
        ],
      },
      {
        title: 'sidebar.sections.hr',
        items: [
          { label: 'sidebar.items.development', href: '/equipe/formations', icon: 'GraduationCap' },
          { label: 'sidebar.items.wellness', href: '/equipe/bien-etre', icon: 'Sun' },
          { label: 'sidebar.items.recruitment', href: '/equipe/recrutement', icon: 'UserPlus' },
          { label: 'sidebar.items.reunions', href: '/equipe/reunions', icon: 'Mic' },
        ],
      },
    ],
  },
  admin: {
    moduleId: 'admin',
    sections: [
      {
        title: 'sidebar.sections.overview',
        items: [
          { label: 'sidebar.items.dashboard', href: '/admin/dashboard', icon: 'Activity' },
          { label: 'sidebar.items.accounts', href: '/admin/accounts', icon: 'Users' },
          { label: 'sidebar.items.apiCosts', href: '/admin/api-usage', icon: 'Zap' },
        ],
      },
      {
        title: 'sidebar.sections.stock',
        items: [
          { label: 'sidebar.items.administration', href: '/admin/import', icon: 'Database' },
          { label: 'sidebar.items.registries', href: '/admin/registries', icon: 'BookOpen' },
          { label: 'sidebar.items.autoTag', href: '/admin/auto-tag', icon: 'Tag' },
          { label: 'sidebar.items.barcodeTest', href: '/admin/barcode-test', icon: 'ScanLine' },
        ],
      },
      {
        title: 'sidebar.sections.docs',
        items: [
          { label: 'sidebar.items.zilloDocsAi', href: '/admin/zillodocs-ai', icon: 'FileText' },
        ],
      },
      {
        title: 'sidebar.sections.equipe',
        items: [
          { label: 'sidebar.items.constantesSociales', href: '/admin/constantes-sociales', icon: 'Calculator' },
        ],
      },
      {
        title: 'sidebar.sections.zilloTv',
        items: [
          { label: 'sidebar.items.clips', href: '/admin/clips', icon: 'Presentation' },
          { label: 'sidebar.items.dynamicClips', href: '/admin/dynamic-clips', icon: 'Sparkles' },
        ],
      },
    ],
  },
  'zillotv': {
    moduleId: 'zillotv',
    sections: [
      {
        title: 'sidebar.sections.zillotv',
        items: [
          { label: 'sidebar.items.dashboard', href: '/zillo-tv', icon: 'LayoutDashboard', end: true },
          { label: 'sidebar.items.mesClips', href: '/zillo-tv/mes-clips', icon: 'Clapperboard' },
          { label: 'sidebar.items.playlists', href: '/zillo-tv/playlists', icon: 'ListVideo' },
          { label: 'sidebar.items.schedule', href: '/zillo-tv/schedule', icon: 'CalendarDays' },
          { label: 'sidebar.items.videos', href: '/zillo-tv/videos', icon: 'Film' },
        { label: 'sidebar.items.screens', href: '/zillo-tv/devices', icon: 'Monitor' },
        ],
      },
    ],
  },
};

// Modules available per entity type (null = all types)
const MODULE_ENTITY_FILTER: Record<string, string[] | null> = {
  stock: null, // all types
  equipe: null,
  zilloteam: null,
  zillotasks: null,
  zillodocs: null,
  admin: null,
  devis: ['cabinet'],
  banking: ['cabinet'],
  analytics: null,
  redactia: ['cabinet'],
  repondeur: ['cabinet'],
  zillotv: null,
};

function filterSidebarByPermissions(
  sidebar: ModuleSidebarConfig,
  hasAccess: (key: string) => boolean,
): ModuleSidebarConfig {
  return {
    ...sidebar,
    sections: sidebar.sections
      .map(section => ({
        ...section,
        items: section.items.filter(item => {
          const permKey = SIDEBAR_PERMISSION_MAP[item.href];
          if (!permKey) return true;
          return hasAccess(permKey);
        }),
      }))
      .filter(section => section.items.length > 0),
  };
}

export function ModuleProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { entityType, account, isSiteAdmin } = useAuth();
  const { hasAccess } = usePermissions();
  const [currentModule, setCurrentModuleState] = useState<ModuleDefinition | null>(null);
  const [currentSidebar, setCurrentSidebar] = useState<ModuleSidebarConfig | null>(null);
  const prevModuleIdRef = useRef<string | null>(null);
  const isFirstRender = useRef(true);
  const [equipeBadges, setEquipeBadges] = useState({ alertes: 0, conges: 0 });

  const enabledModules = account?.enabledModules ?? [];

  // All modules for this entity type (not filtered by enabled_modules)
  const allEntityModules = useMemo(() => {
    return defaultModules.filter(m => {
      if (m.id === 'admin') return true;
      const allowed = MODULE_ENTITY_FILTER[m.id];
      if (allowed !== null && allowed !== undefined && !allowed.includes(entityType)) return false;
      return true;
    });
  }, [entityType]);

  // Only modules the account has access to
  const filteredModules = useMemo(() => {
    // Modules always visible to all users (no need to be in enabled_modules)
    const alwaysVisible = ['admin'];
    return allEntityModules.filter(m => {
      if (alwaysVisible.includes(m.id)) return true;
      if (isSiteAdmin) return true;
      return enabledModules.includes(m.id);
    });
  }, [allEntityModules, enabledModules, isSiteAdmin]);

  const fetchEquipeBadges = useCallback(() => {
    api.getEquipeBadges().then(r => {
      if (r.success && r.data) {
        setEquipeBadges({ alertes: r.data.alertes_actives, conges: r.data.conges_attente });
      }
    }).catch(() => {/* silently ignore */});
  }, []);

  // Auto-detect module from URL
  useEffect(() => {
    let module = defaultModules.find((m) =>
      location.pathname.startsWith(m.basePath)
    );

    // Site admins on /profile: force admin module so sidebar stays visible
    if (!module && isSiteAdmin && location.pathname === '/profile') {
      module = defaultModules.find((m) => m.id === 'admin');
    }

    if (module) {
      setCurrentModuleState(module);

      if (module.id === 'equipe') {
        fetchEquipeBadges();
      }

      const baseSidebar = defaultSidebars[module.id] || null;
      setCurrentSidebar(baseSidebar ? filterSidebarByPermissions(baseSidebar, hasAccess) : null);

      // No module-switch toast — it's distracting
      prevModuleIdRef.current = module.id;
    } else {
      prevModuleIdRef.current = null;
      setCurrentModuleState(null);
      setCurrentSidebar(null);
    }
    isFirstRender.current = false;
  }, [location.pathname, fetchEquipeBadges, hasAccess]);

  // Inject dynamic badges into equipe sidebar
  useEffect(() => {
    if (currentModule?.id !== 'equipe') return;
    const base = defaultSidebars['equipe'];
    if (!base) return;
    const withBadges: ModuleSidebarConfig = {
      ...base,
      sections: base.sections.map(section => ({
        ...section,
        items: section.items.map(item => {
          if (item.href === '/equipe/alertes') return { ...item, badge: equipeBadges.alertes || undefined };
          if (item.href === '/equipe/conges') return { ...item, badge: equipeBadges.conges || undefined };
          return item;
        }),
      })),
    };
    setCurrentSidebar(filterSidebarByPermissions(withBadges, hasAccess));
  }, [equipeBadges, currentModule?.id, hasAccess]);

  const setCurrentModule = (moduleId: string | null) => {
    if (moduleId) {
      const module = defaultModules.find((m) => m.id === moduleId);
      setCurrentModuleState(module || null);
      const sb = module ? defaultSidebars[module.id] || null : null;
      setCurrentSidebar(sb ? filterSidebarByPermissions(sb, hasAccess) : null);
    } else {
      setCurrentModuleState(null);
      setCurrentSidebar(null);
    }
  };

  return (
    <ModuleContext.Provider
      value={{
        currentModule,
        currentSidebar,
        availableModules: filteredModules,
        allEntityModules,
        setCurrentModule,
      }}
    >
      {children}
    </ModuleContext.Provider>
  );
}

export function useModules() {
  const context = useContext(ModuleContext);
  if (context === undefined) {
    throw new Error('useModules must be used within a ModuleProvider');
  }
  return context;
}

export default ModuleContext;
